import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/common/Modal';

const InternalSiteManagement = () => {
  const { currentUser: user } = useAuth();
  const [internalSites, setInternalSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    location: '',
    manager: '',
  });
  const [selectedSite, setSelectedSite] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    // Only directrice_marketing should access this page
    if (user && user.role !== 'directrice_marketing') {
      window.location.href = '/dashboard';
    } else {
      fetchInternalSites();
    }
  }, [user, showInactive]);

  const fetchInternalSites = async () => {
    try {
      setLoading(true);
      const response = await api.internalSites.getAll(showInactive);
      setInternalSites(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching internal sites:', error);
      toast.error('Erreur lors du chargement des sites internes');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleAddSite = async (e) => {
    e.preventDefault();
    
    try {
      setFormLoading(true);
      
      await api.internalSites.create(formData);
      
      toast.success('Site interne ajouté avec succès');
      setShowAddModal(false);
      setFormData({
        code: '',
        name: '',
        location: '',
        manager: '',
      });
      fetchInternalSites();
      setFormLoading(false);
    } catch (error) {
      console.error('Error adding internal site:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'ajout du site interne');
      setFormLoading(false);
    }
  };

  const handleEditSite = async (e) => {
    e.preventDefault();
    
    if (!selectedSite) return;
    
    try {
      setFormLoading(true);
      
      await api.internalSites.update(selectedSite._id, formData);
      
      toast.success('Site interne mis à jour avec succès');
      setShowEditModal(false);
      fetchInternalSites();
      setFormLoading(false);
    } catch (error) {
      console.error('Error updating internal site:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la mise à jour du site interne');
      setFormLoading(false);
    }
  };

  const handleDeleteSite = async () => {
    if (!selectedSite) return;
    
    try {
      setFormLoading(true);
      
      await api.internalSites.deactivate(selectedSite._id);
      
      toast.success('Site interne désactivé avec succès');
      setShowDeleteModal(false);
      fetchInternalSites();
      setFormLoading(false);
    } catch (error) {
      console.error('Error deactivating internal site:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la désactivation du site interne');
      setFormLoading(false);
    }
  };

  const handleInitializeSites = async () => {
    try {
      setLoading(true);
      
      await api.internalSites.initialize();
      
      toast.success('Sites internes initialisés avec succès');
      fetchInternalSites();
    } catch (error) {
      console.error('Error initializing internal sites:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'initialisation des sites internes');
      setLoading(false);
    }
  };

  const openEditModal = (site) => {
    setSelectedSite(site);
    setFormData({
      code: site.code,
      name: site.name,
      location: site.location || '',
      manager: site.manager || '',
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (site) => {
    setSelectedSite(site);
    setShowDeleteModal(true);
  };

  if (loading && internalSites.length === 0) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestion des sites internes</h1>
        <div className="flex space-x-2">
          <button
            onClick={handleInitializeSites}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Initialiser les sites par défaut
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Ajouter un site interne
          </button>
        </div>
      </div>
      
      <div className="mb-6 flex justify-end">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="showInactive"
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            checked={showInactive}
            onChange={() => setShowInactive(!showInactive)}
          />
          <label htmlFor="showInactive" className="ml-2 block text-sm text-gray-900">
            Afficher les sites inactifs
          </label>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Emplacement</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Responsable</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {internalSites.length > 0 ? (
                internalSites.map((site) => (
                  <tr key={site._id} className={`hover:bg-gray-50 ${!site.isActive ? 'bg-gray-100' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{site.code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{site.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{site.location || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{site.manager || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {site.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Actif
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Inactif
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openEditModal(site)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Modifier
                      </button>
                      {site.isActive && (
                        <button
                          onClick={() => openDeleteModal(site)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Désactiver
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                    Aucun site interne trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Add Internal Site Modal */}
      {showAddModal && (
        <Modal
          open
          onClose={() => setShowAddModal(false)}
          title="Ajouter un site interne"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowAddModal(false)}
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="submit"
                form="site-add-form"
                className="modal-btn-primary"
                disabled={formLoading}
              >
                {formLoading ? 'Ajout...' : 'Ajouter'}
              </button>
            </>
          }
        >
          <form id="site-add-form" onSubmit={handleAddSite}>
            <div className="space-y-4">
                        <div>
                          <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                            Code
                          </label>
                          <input
                            type="text"
                            name="code"
                            id="code"
                            value={formData.code}
                            onChange={handleChange}
                            required
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                            Nom
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                            Emplacement
                          </label>
                          <input
                            type="text"
                            name="location"
                            id="location"
                            value={formData.location}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="manager" className="block text-sm font-medium text-gray-700">
                            Responsable
                          </label>
                          <input
                            type="text"
                            name="manager"
                            id="manager"
                            value={formData.manager}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Internal Site Modal */}
      {showEditModal && selectedSite && (
        <Modal
          open
          onClose={() => setShowEditModal(false)}
          title="Modifier le site interne"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowEditModal(false)}
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="submit"
                form="site-edit-form"
                className="modal-btn-primary"
                disabled={formLoading}
              >
                {formLoading ? 'Mise à jour...' : 'Mettre à jour'}
              </button>
            </>
          }
        >
          <form id="site-edit-form" onSubmit={handleEditSite}>
            <div className="space-y-4">
                        <div>
                          <label htmlFor="edit-code" className="block text-sm font-medium text-gray-700">
                            Code
                          </label>
                          <input
                            type="text"
                            name="code"
                            id="edit-code"
                            value={formData.code}
                            onChange={handleChange}
                            required
                            disabled
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md bg-gray-100"
                          />
                          <p className="mt-1 text-xs text-gray-500">Le code ne peut pas être modifié</p>
                        </div>
                        <div>
                          <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                            Nom
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="edit-name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-location" className="block text-sm font-medium text-gray-700">
                            Emplacement
                          </label>
                          <input
                            type="text"
                            name="location"
                            id="edit-location"
                            value={formData.location}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-manager" className="block text-sm font-medium text-gray-700">
                            Responsable
                          </label>
                          <input
                            type="text"
                            name="manager"
                            id="edit-manager"
                            value={formData.manager}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Internal Site Modal */}
      {showDeleteModal && selectedSite && (
        <Modal
          open
          onClose={() => setShowDeleteModal(false)}
          title="Désactiver le site interne"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className="modal-btn-danger"
                onClick={handleDeleteSite}
                disabled={formLoading}
              >
                {formLoading ? 'Désactivation...' : 'Désactiver'}
              </button>
            </>
          }
        >
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <p className="text-sm text-gray-500">
                Êtes-vous sûr de vouloir désactiver le site interne {selectedSite.name} ? Cette action peut être annulée ultérieurement.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default InternalSiteManagement;
